/**
 * State Contracts (SMAC Register) — the compliance view of the org's State
 * Medicaid Agency Contracts and every obligation inside them.
 *
 * The experience, deliberately simple: upload the contract once, read what
 * the app found (each duty beside the contract's own words), assign a person
 * and a rhythm, activate. From then on this tab is a scoreboard: green means
 * evidenced on time, amber means due soon, red means overdue or unassigned.
 * Owners never see this screen — their items reach them on their home page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import ThreadPanel from './components/ThreadPanel';

const API = process.env.REACT_APP_API_URL;

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'];

const CADENCE_LABEL = { once: 'One time', monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Twice a year', annual: 'Yearly' };
const CADENCE_MONTHS = { monthly: 1, quarterly: 3, semiannual: 6, annual: 12 };

// "Quarterly. After each completion the next lands automatically: Jun 30, Sep 30, Dec 31."
// Mirrors the server's nextDueAfter stepping (whole months in UTC), so the
// dates shown are the dates the series will actually produce.
function schedulePreview(cadence, firstDue) {
  const months = CADENCE_MONTHS[cadence];
  if (!months || !firstDue) return null;
  const d = new Date(firstDue + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return null;
  const nexts = [1, 2, 3].map(i => {
    const r = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months * i, d.getUTCDate()));
    return r.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  });
  return `${CADENCE_LABEL[cadence]}. After each completion the next lands automatically: ${nexts.join(', ')}…`;
}
const CATEGORY_LABEL = { dated: 'Deliverable', conditional: 'When it happens', monitorable: 'Ongoing duty', structural: 'Standing requirement' };

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(String(iso).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

export default function SmacRegister() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [regTab, setRegTab] = useState('deliverables');

  const loadContracts = useCallback(async () => {
    try {
      const d = await fetch(`${API}/api/smac/contracts`, { credentials: 'include' }).then(r => r.json());
      if (d.success) setContracts(d.contracts || []);
    } finally { setLoading(false); }
  }, []);

  const loadDetail = useCallback(async (id) => {
    const d = await fetch(`${API}/api/smac/contracts/${id}`, { credentials: 'include' }).then(r => r.json());
    if (d.success) setDetail(d);
  }, []);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  // While any contract is still being read in the background, poll the list
  // so the row flips to "ready to review" on its own.
  const anyProcessing = contracts.some(c => c.status === 'processing');
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const t = setInterval(loadContracts, 5000);
    return () => clearInterval(t);
  }, [anyProcessing, loadContracts]);
  useEffect(() => {
    fetch(`${API}/api/calendar/assignable-users`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : (d.users || [])))
      .catch(() => {});
  }, []);
  useEffect(() => { if (openId) loadDetail(openId); else setDetail(null); }, [openId, loadDetail]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = e.target;
    const file = form.contract.files[0];
    if (!file) { setUploadError('Attach the contract PDF.'); return; }
    setUploading(true); setUploadError('');
    const fd = new FormData();
    fd.append('contract', file);
    fd.append('state_code', form.state_code.value);
    fd.append('name', form.name.value || file.name);
    if (form.contract_year.value) fd.append('contract_year', form.contract_year.value);
    try {
      const d = await fetch(`${API}/api/smac/contracts`, { method: 'POST', credentials: 'include', body: fd }).then(r => r.json());
      if (d.success) { setShowUpload(false); await loadContracts(); }
      else setUploadError(d.message || 'The contract could not be saved.');
    } catch { setUploadError('The upload did not reach the server. Check your connection and try again.'); }
    setUploading(false);
  };

  const retryExtraction = async (id) => {
    await fetch(`${API}/api/smac/contracts/${id}/reextract`, { method: 'POST', credentials: 'include' }).catch(() => {});
    await loadContracts();
  };

  // Wipes the contract's current reading (obligations + their calendar items)
  // and reads it again under the current rules. The uploaded file survives.
  // Destructive for the derived rows, so the button asks in place first —
  // a calm inline confirm, not a browser dialog.
  const [rereadArmId, setRereadArmId] = useState(null);
  const [rereadError, setRereadError] = useState('');
  const rereadContract = async (c) => {
    if (rereadArmId !== c.id) { setRereadArmId(c.id); setRereadError(''); return; }
    setRereadArmId(null);
    const d = await fetch(`${API}/api/smac/contracts/${c.id}/reread`, { method: 'POST', credentials: 'include' })
      .then(r => r.json()).catch(() => null);
    if (!d?.success) {
      setRereadError(d?.message || 'The re-read could not be started. Nothing was removed.');
      return;
    }
    setOpenId(null); setDetail(null);
    await loadContracts();
  };

  // Permanent removal, so it arms in place like Re-read does. Unlike Re-read
  // it does NOT read the file again — this is how a superseded contract (a
  // prior year's SMAC, or one uploaded twice) finally leaves the Hub, instead
  // of leaving its accepted obligations on owners' calendars forever.
  const [deleteArmId, setDeleteArmId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const deleteContract = async (c) => {
    if (deleteArmId !== c.id) { setDeleteArmId(c.id); setDeleteError(''); return; }
    setDeleteArmId(null);
    const d = await fetch(`${API}/api/smac/contracts/${c.id}`, { method: 'DELETE', credentials: 'include' })
      .then(r => r.json()).catch(() => null);
    if (!d?.success) {
      setDeleteError(d?.message || 'The contract could not be deleted. Nothing was removed.');
      return;
    }
    setOpenId(null); setDetail(null);
    await loadContracts();
  };

  const patchObligation = async (id, body) => {
    const d = await fetch(`${API}/api/smac/obligations/${id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then(r => r.json());
    if (d.success && openId) await loadDetail(openId);
    return d;
  };

  // Manual reminders: select active obligations, email their owners now.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [reminderNote, setReminderNote] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const sendReminders = async () => {
    if (!selectedIds.size) return;
    setSendingReminders(true); setReminderNote('');
    try {
      const d = await fetch(`${API}/api/smac/obligations/remind`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ obligation_ids: [...selectedIds] }),
      }).then(r => r.json());
      if (d.success) {
        const parts = [`${d.sent} reminder${d.sent === 1 ? '' : 's'} sent`];
        if (d.no_owner) parts.push(`${d.no_owner} skipped (no owner assigned)`);
        if (d.failed) parts.push(`${d.failed} failed`);
        setReminderNote(parts.join(', ') + '.');
        setSelectedIds(new Set());
      } else {
        setReminderNote(d.message || 'The reminders could not be sent.');
      }
    } catch {
      setReminderNote('The reminders could not be sent.');
    }
    setSendingReminders(false);
  };

  // ── Contract list ─────────────────────────────────────────────────────────
  if (!openId) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="text-lg font-bold text-slate-800">State Contracts</h2>
            <p className="text-sm text-slate-500 mt-0.5">Your State Medicaid Agency Contracts, and whether every promise in them is assigned, on schedule, and evidenced.</p>
          </div>
          <button type="button" onClick={() => setShowUpload(v => !v)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium shadow-sm hover:opacity-90 transition">
            Add a contract
          </button>
        </div>

        {showUpload && (
          <form onSubmit={handleUpload} className="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50/60 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
              <select name="state_code" required className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                <option value="">Pick…</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
              <input name="name" type="text" placeholder="e.g. Florida SMAC 2027" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contract year</label>
              <input name="contract_year" type="number" min="2020" max="2035" className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contract PDF</label>
              <input name="contract" type="file" accept=".pdf" className="text-sm" />
            </div>
            <button type="submit" disabled={uploading}
              className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm font-medium disabled:opacity-50">
              {uploading ? 'Saving…' : 'Upload & read'}
            </button>
            {uploadError && <p className="w-full text-sm text-red-600">{uploadError}</p>}
            {uploading && <p className="w-full text-xs text-slate-500">Saving the contract. The reading happens in the background — a long contract can take several minutes, and you can leave this page.</p>}
          </form>
        )}

        {rereadError && <p className="mt-3 text-sm text-red-600">{rereadError}</p>}
        {deleteError && <p className="mt-3 text-sm text-red-600">{deleteError}</p>}

        <div className="mt-5">
          {loading ? (
            <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
          ) : contracts.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-[15px] font-medium text-slate-800">No state contracts yet.</p>
              <p className="text-sm text-slate-500 mt-1">Add your SMAC and the app will read it into a working register.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
              {contracts.map(c => (
                <button key={c.id} type="button"
                  onClick={() => { if (c.status !== 'processing' && c.status !== 'failed') setOpenId(c.id); }}
                  className={`w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left transition ${
                    c.status === 'processing' || c.status === 'failed' ? 'cursor-default' : 'hover:bg-slate-50'
                  }`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.state_code}{c.contract_year ? ` · CY ${c.contract_year}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 text-xs">
                    {c.status === 'processing' && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                        Reading the contract…
                      </span>
                    )}
                    {c.status === 'failed' && (
                      <>
                        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">Could not be read</span>
                        <span role="button" tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); retryExtraction(c.id); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); retryExtraction(c.id); } }}
                          className="px-2 py-0.5 rounded-full bg-slate-800 text-white font-medium cursor-pointer">
                          Try again
                        </span>
                      </>
                    )}
                    {c.status !== 'processing' && c.status !== 'failed' && (
                      <>
                        {c.score.suggested > 0 && <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-medium">{c.score.suggested} to review</span>}
                        {c.score.overdue > 0 && <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">{c.score.overdue} overdue</span>}
                        {c.score.unassigned > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">{c.score.unassigned} unassigned</span>}
                        {c.score.active > 0 && c.score.overdue === 0 && c.score.unassigned === 0 && c.score.suggested === 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">On track</span>
                        )}
                        <span className="text-slate-400">{c.score.active} active</span>
                        {rereadArmId === c.id ? (
                          <>
                            <span role="button" tabIndex={0}
                              title="Yes — remove this reading and read the contract again. The uploaded file is kept."
                              onClick={(e) => { e.stopPropagation(); rereadContract(c); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); rereadContract(c); } }}
                              className="px-2 py-0.5 rounded-full bg-red-600 text-white font-medium cursor-pointer">
                              Yes, start over
                            </span>
                            <span role="button" tabIndex={0}
                              title="Keep the current reading"
                              onClick={(e) => { e.stopPropagation(); setRereadArmId(null); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setRereadArmId(null); } }}
                              className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-medium cursor-pointer">
                              Keep
                            </span>
                          </>
                        ) : deleteArmId === c.id ? (
                          <>
                            <span role="button" tabIndex={0}
                              title="Yes — delete this contract and everything read from it: obligations, calendar items, assignments, history and reconciliation findings. This cannot be undone."
                              onClick={(e) => { e.stopPropagation(); deleteContract(c); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); deleteContract(c); } }}
                              className="px-2 py-0.5 rounded-full bg-red-700 text-white font-medium cursor-pointer">
                              Yes, delete everything
                            </span>
                            <span role="button" tabIndex={0}
                              title="Keep this contract"
                              onClick={(e) => { e.stopPropagation(); setDeleteArmId(null); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setDeleteArmId(null); } }}
                              className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-medium cursor-pointer">
                              Keep
                            </span>
                          </>
                        ) : (
                          <>
                            <span role="button" tabIndex={0}
                              title="Remove every obligation from this reading (calendar items, assignments and history included) and read the contract again. The uploaded contract file is kept."
                              onClick={(e) => { e.stopPropagation(); rereadContract(c); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); rereadContract(c); } }}
                              className="px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100 font-medium cursor-pointer">
                              Re-read
                            </span>
                            <span role="button" tabIndex={0}
                              title="Delete this contract permanently, with every obligation, calendar item, assignment and reconciliation finding that came from it. Use this for a superseded contract you no longer track."
                              onClick={(e) => { e.stopPropagation(); deleteContract(c); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); deleteContract(c); } }}
                              className="px-2 py-0.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50 font-medium cursor-pointer">
                              Delete
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── One contract's register ───────────────────────────────────────────────
  const score = detail?.score;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
      <button type="button" onClick={() => { setOpenId(null); loadContracts(); }}
        className="text-sm text-slate-500 hover:text-slate-700 mb-3">← All state contracts</button>

      {!detail ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">{detail.contract.name}</h2>
              <p className="text-sm text-slate-500 mt-0.5">{detail.contract.state_code}{detail.contract.contract_year ? ` · CY ${detail.contract.contract_year}` : ''}</p>
            </div>
            {score && (
              <div className="flex gap-5 text-center">
                <div><p className="text-xl font-bold text-slate-800">{score.active}</p><p className="text-[11px] text-slate-500">Active</p></div>
                <div><p className={`text-xl font-bold ${score.coverage_pct === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>{score.coverage_pct != null ? `${score.coverage_pct}%` : '—'}</p><p className="text-[11px] text-slate-500">Assigned</p></div>
                <div><p className={`text-xl font-bold ${score.overdue ? 'text-red-600' : 'text-emerald-600'}`}>{score.overdue}</p><p className="text-[11px] text-slate-500">Overdue</p></div>
                <div><p className="text-xl font-bold text-slate-800">{score.evidence_events}</p><p className="text-[11px] text-slate-500">Evidence records</p></div>
              </div>
            )}
          </div>

          {(detail.obligations || []).some(ob => ob.status === 'active') && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button type="button" disabled={!selectedIds.size || sendingReminders} onClick={sendReminders}
                className="px-3.5 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-40">
                {sendingReminders ? 'Sending…' : `Email a reminder to the owner${selectedIds.size > 1 ? 's' : ''}${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
              </button>
              <span className="text-xs text-slate-500">Select active obligations below, then send. Owners receive the item, its due date, and a link to complete it.</span>
              {reminderNote && <span className="text-xs font-medium text-emerald-700">{reminderNote}</span>}
            </div>
          )}

          {(() => {
            // Deliverables are the working list; standing duties are the
            // contract's promises about how the plan operates. Mixing them
            // made "Maintain HIPAA policies" sit beside "Submit the network
            // plan by September 1" as if both were filings — they are not,
            // and the register now says so with its structure.
            const all = detail.obligations || [];
            const deliverables = all.filter(o => o.category === 'dated');
            const conditionals = all.filter(o => o.category === 'conditional');
            const duties = all.filter(o => !['dated', 'conditional'].includes(o.category));
            const penalties = detail.penalties || [];
            const penaltyLinks = detail.penalty_links || [];
            const reportedEvents = detail.reported_events || [];
            const row = (ob) => (
              <ObligationRow key={ob.id} ob={ob} users={users} onPatch={patchObligation}
                selected={selectedIds.has(ob.id)} onToggleSelected={toggleSelected}
                penalties={penalties} penaltyLinks={penaltyLinks}
                reportedEvents={reportedEvents.filter(e => e.obligation_id === ob.id)}
                onReported={() => loadDetail(openId)}
                onDecided={() => loadDetail(openId)} />
            );
            return (
              <div className="mt-5">
                {/* The register's three natures, each on its own tab: the
                    tables' true deliverables, the if-then register, and the
                    contract's standing promises. Never mixed again. */}
                <div className="inline-flex gap-1 bg-slate-100 rounded-xl p-1 mb-4">
                  {[
                    { key: 'deliverables', label: `Deliverables — Tables 6 & 7 (${deliverables.length})` },
                    { key: 'triggered', label: `When it happens (${conditionals.length})` },
                    { key: 'duties', label: `Standing duties (${duties.length})` },
                  ].map(t => (
                    <button key={t.key} type="button" onClick={() => setRegTab(t.key)}
                      className={`px-4 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                        regTab === t.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {regTab === 'deliverables' && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2.5">
                      The contract's own filing lists — real dates and cycles owed to the state, cadence and first due pre-filled from the contract. These drive the calendar.
                    </p>
                    <div className="space-y-3">
                      {deliverables.map(row)}
                      {deliverables.length === 0 && (
                        <p className="text-sm text-slate-500 py-4 text-center">No deliverables in this reading.</p>
                      )}
                    </div>
                  </div>
                )}

                {regTab === 'triggered' && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2.5">
                      Owed only when the trigger occurs. No dates, no overdue — reporting an event creates the real, dated duty with the contract's own deadline.
                    </p>
                    <div className="space-y-3">
                      {conditionals.map(row)}
                      {conditionals.length === 0 && (
                        <p className="text-sm text-slate-500 py-4 text-center">No event-triggered duties in this reading.</p>
                      )}
                    </div>
                  </div>
                )}

                {regTab === 'duties' && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2.5">
                      Ongoing and structural requirements — tracked and attestable, never calendar deadlines.
                    </p>
                    <div className="space-y-3">
                      {duties.map(row)}
                      {duties.length === 0 && (
                        <p className="text-sm text-slate-500 py-4 text-center">No standing duties in this reading.</p>
                      )}
                    </div>
                  </div>
                )}

                {all.length === 0 && (
                  <p className="text-sm text-slate-500 py-6 text-center">No obligations were extracted from this contract.</p>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

/**
 * Report an event on a when-it-happens condition. One minute when reality
 * happens, zero clicks when it doesn't: pick the date, optionally say what
 * occurred, and the duty spawns with its due date computed from the
 * contract's own SLA. The preview states exactly what will be created
 * before anything is.
 */
function ReportEventPanel({ ob, onReported }) {
  const [open, setOpen] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const submit = async () => {
    setBusy(true); setError('');
    const r = await fetch(`${API}/api/smac/obligations/${ob.id}/report-event`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_date: eventDate, note: note || null }),
    }).then(x => x.json()).catch(() => ({ success: false, message: 'Network error' }));
    setBusy(false);
    if (!r.success) { setError(r.message || 'Could not record the event'); return; }
    setDone(r.computed_due);
    setOpen(false); setEventDate(''); setNote('');
    if (onReported) onReported();
  };

  if (done !== undefined && done !== null) {
    return (
      <p className="mt-1.5 text-xs text-emerald-700 font-medium">
        Event recorded — the duty is on the calendar, due {fmtDate(done)}.
      </p>
    );
  }

  return (
    <div className="mt-1.5">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
          className="text-xs font-semibold text-orange-700 hover:text-orange-800 border border-orange-200 rounded-lg px-2.5 py-1 hover:bg-orange-50">
          Report an event
        </button>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2 max-w-md">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">When did it happen?</label>
              <input type="date" value={eventDate} max={new Date().toISOString().slice(0, 10)}
                onChange={e => setEventDate(e.target.value)}
                className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[11px] text-slate-500 mb-1">What happened (optional)</label>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="One line for the record"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white" />
            </div>
          </div>
          {eventDate && ob.sla_days && (
            <p className="text-[11px] text-slate-600">
              This creates real, dated work: <span className="font-semibold">{ob.title}</span>, due per the
              contract's own timeline ({ob.sla_text || `${ob.sla_days} ${ob.sla_days_kind || 'calendar'} days`}), on the calendar the moment you save.
            </p>
          )}
          {eventDate && !ob.sla_days && (
            <p className="text-[11px] text-slate-600">
              The contract states no computable day count for this one — the event is recorded, and compliance sets the duty's date.
            </p>
          )}
          {error && <p className="text-[11px] text-red-600">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setOpen(false); setError(''); }}
              className="px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button type="button" disabled={busy || !eventDate} onClick={submit}
              className="px-3 py-1 text-xs font-semibold rounded-lg bg-orange-600 text-white disabled:opacity-50">
              {busy ? 'Recording…' : 'Report event & create the duty'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Commits only on blur or Enter, never per keystroke — a date input emits
// partial years while the user is typing ("...26" is the year 0026), and
// saving those live is how an obligation ends up two millennia overdue.
function DueDateInput({ value, onCommit }) {
  const current = value ? String(value).slice(0, 10) : '';
  const [v, setV] = useState(current);
  useEffect(() => { setV(current); }, [current]);
  const commit = () => {
    if (v === current) return;
    if (v && (v.length !== 10 || v < '2000-01-01' || v > '2100-12-31')) { setV(current); return; }
    onCommit(v || null);
  };
  return (
    <input
      type="date" value={v} min="2000-01-01" max="2100-12-31"
      onChange={e => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white"
    />
  );
}

const POSTURE_LABEL = {
  coordination_only: 'Coordination-only D-SNP',
  hide: 'HIDE — highly integrated',
  fide: 'FIDE — fully integrated',
  unclear: 'Integration level unclear',
};

/**
 * The D-SNP plan advisor, on one deliverable. Runs on demand, shows the
 * integration-posture finding first (everything hangs on it), then the plan,
 * every claim carrying its quote and a verified / unverified mark from the
 * server's own re-check. Exported — the owner's modal on Workspace uses it too.
 */
export function AdvicePanel({ obligationId, canRun = true }) {
  const [advice, setAdvice] = useState(undefined);   // undefined=not loaded, null=none yet
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/smac/obligations/${obligationId}/advice`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (alive) setAdvice(d?.success ? d.advice : null); })
      .catch(() => { if (alive) setAdvice(null); });
    return () => { alive = false; };
  }, [obligationId]);

  const run = async () => {
    setRunning(true); setError('');
    const d = await fetch(`${API}/api/smac/obligations/${obligationId}/advise`, {
      method: 'POST', credentials: 'include',
    }).then(r => r.json()).catch(() => null);
    if (d?.success) {
      setAdvice({ advice: d.advice, integration_posture: d.integration_posture,
        quotes_total: d.quotes_total, quotes_verified: d.quotes_verified,
        declined: d.declined, created_at: d.created_at });
    } else setError(d?.message || 'The advisor could not finish. Nothing was changed.');
    setRunning(false);
  };

  const body = advice?.advice;
  const Ground = ({ g }) => (
    <p className={`text-[11.5px] mt-1 pl-3 border-l-2 ${g.verified ? 'border-emerald-300 text-slate-500' : 'border-amber-300 text-amber-700'}`}>
      “{g.quote}” <span className="not-italic">— {g.source === 'contract' ? 'the contract' : `42 CFR ${g.citation || ''}`}
      {g.verified ? '' : ' · could not be verified word-for-word, check the source'}</span>
    </p>
  );

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Plan advisor</p>
        {canRun && (
          <button type="button" disabled={running} onClick={run}
            title="Read the contract and the regulation behind this deliverable and propose a plan. Advice only — it changes nothing."
            className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">
            {running ? 'Reading the contract and the regulation…' : (body ? 'Ask again' : 'Advise me on this deliverable')}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {advice === undefined && !error && <p className="text-xs text-slate-400 mt-2">Loading…</p>}
      {advice === null && !running && !error && (
        <p className="text-xs text-slate-500 mt-2">No advice yet. The advisor reads your uploaded contract and 42 CFR 422/438 — nothing else — and every quote it shows is checked word-for-word.</p>
      )}

      {body && body.declined && (
        <p className="text-[13px] text-slate-700 mt-3">
          The advisor declined rather than guess: {body.declined_reason || 'the contract text does not settle this.'}
        </p>
      )}

      {body && !body.declined && (
        <div className="mt-3 space-y-4">
          <div>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
              advice.integration_posture === 'unclear'
                ? 'bg-amber-50 text-amber-800 border-amber-200'
                : 'bg-indigo-50 text-indigo-800 border-indigo-200'}`}>
              {POSTURE_LABEL[advice.integration_posture] || advice.integration_posture}
              {body.mma_companion === true ? ' · companion Medicaid plan' : body.mma_companion === false ? ' · no companion Medicaid plan' : ''}
            </span>
            {(body.posture_evidence || []).map((g, i) => <Ground key={i} g={g} />)}
            {body.posture_caveat && <p className="text-[11.5px] text-slate-500 mt-1.5 italic">{body.posture_caveat}</p>}
          </div>

          {body.what_is_owed && (
            <div className="text-[13px] text-slate-700">
              <p><span className="font-semibold">What is owed:</span> {body.what_is_owed.artifact}</p>
              <p><span className="font-semibold">To:</span> {body.what_is_owed.recipient} <span className="font-semibold ml-2">When:</span> {body.what_is_owed.timing}</p>
              {body.what_is_owed.format_requirements && <p className="text-slate-600 mt-0.5">{body.what_is_owed.format_requirements}</p>}
            </div>
          )}

          {(body.plan_steps || []).length > 0 && (
            <ol className="space-y-2.5">
              {body.plan_steps.map((s, i) => (
                <li key={i} className="text-[13px] text-slate-700">
                  <span className="font-semibold">{s.step || i + 1}. {s.action}</span>
                  {s.owner_hint && <span className="text-slate-500"> — usually {s.owner_hint}</span>}
                  {(s.grounds || []).map((g, j) => <Ground key={j} g={g} />)}
                </li>
              ))}
            </ol>
          )}

          {(body.risks || []).length > 0 && (
            <p className="text-[12px] text-slate-600"><span className="font-semibold">Watch for:</span> {body.risks.join(' · ')}</p>
          )}

          <p className="text-[11px] text-slate-400">
            {advice.quotes_verified} of {advice.quotes_total} quotes verified word-for-word ·
            advice only — nothing was scheduled, completed or assigned ·
            {' '}{new Date(advice.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
        </div>
      )}
    </div>
  );
}

/** Compliance decides a pending closure request, in place, with a reason on decline. */
function ClosureDecision({ obligationId, onDecided }) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const decide = async (approve) => {
    setBusy(true); setError('');
    const d = await fetch(`${API}/api/smac/obligations/${obligationId}/closure-decision`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approve, note: reason.trim() || null }),
    }).then(r => r.json()).catch(() => null);
    if (d?.success) onDecided?.();
    else setError(d?.message || 'The decision could not be recorded.');
    setBusy(false);
  };

  return (
    <div className="mt-2.5 rounded-xl bg-violet-50/60 border border-violet-200 px-3.5 py-2.5">
      <p className="text-xs text-violet-800 font-medium">The owner asked for this item to be closed.</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy} onClick={() => decide(true)}
          title="Agree and complete the item. Recurring items schedule their next occurrence."
          className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-40">
          Approve & close
        </button>
        {!declining ? (
          <button type="button" disabled={busy} onClick={() => setDeclining(true)}
            title="Keep the item open and tell the owner why"
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-600 hover:bg-white disabled:opacity-40">
            Decline…
          </button>
        ) : (
          <>
            <input value={reason} onChange={e => setReason(e.target.value)} autoFocus
              placeholder="Why it stays open — the owner sees this"
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs min-w-[220px] outline-none focus:ring-2 focus:ring-violet-300" />
            <button type="button" disabled={busy || !reason.trim()} onClick={() => decide(false)}
              className="px-3 py-1.5 rounded-lg bg-violet-700 text-white text-xs font-semibold disabled:opacity-40">
              Send
            </button>
          </>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
    </div>
  );
}

function ObligationRow({ ob, users, onPatch, selected = false, onToggleSelected = null, onDecided = null,
  penalties = [], penaltyLinks = [], reportedEvents = [], onReported = null }) {
  const [showQuote, setShowQuote] = useState(false);
  const [saving, setSaving] = useState(false);
  const myPenalties = penaltyLinks
    .filter(l => l.obligation_id === ob.id)
    .map(l => penalties.find(p => p.id === l.penalty_id))
    .filter(Boolean);
  const suggested = ob.status === 'suggested';
  const overdue = ob.status === 'active' && ob.next_due_date && String(ob.next_due_date).slice(0, 10) < new Date().toISOString().slice(0, 10);

  const save = async (body) => { setSaving(true); await onPatch(ob.id, body); setSaving(false); };

  return (
    <div className={`rounded-xl border p-4 ${suggested ? 'border-sky-200 bg-sky-50/40' : overdue ? 'border-red-200 bg-red-50/30' : 'border-slate-200'} ${selected ? 'ring-2 ring-orange-300' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        {ob.status === 'active' && onToggleSelected && (
          <input type="checkbox" checked={selected} onChange={() => onToggleSelected(ob.id)}
            aria-label={`Select ${ob.title} for a reminder`}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-400" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-800">{ob.title}</p>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">{CATEGORY_LABEL[ob.category] || ob.category}</span>
            {suggested && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700 border border-sky-200">Needs your review</span>}
            {ob.status === 'retired' && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-400 border border-slate-200">Retired</span>}
            {overdue && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">Overdue</span>}
            {ob.flags > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">{ob.flags} flag{ob.flags === 1 ? '' : 's'}</span>}
            {ob.closure_pending && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700 border border-violet-200">Closure requested</span>}
          </div>
          <p className="text-sm text-slate-600 mt-1">{ob.obligation_text}</p>

          {/* The owner asked for this to be closed. Approve completes it through
              the normal evidence path; declining needs a reason they will see. */}
          {ob.closure_pending && ob.status === 'active' && (
            <ClosureDecision obligationId={ob.id} onDecided={onDecided} />
          )}

          {/* The conversation with the owner, on any active item. */}
          {ob.status === 'active' && <ThreadPanel obligationId={ob.id} />}

          {/* The plan advisor exists only for deliverables — items with an
              artifact, a recipient and a date. Standing duties have no plan
              to advise on. */}
          {ob.category === 'dated' && ob.status === 'active' && (
            <AdvicePanel obligationId={ob.id} />
          )}
          {/* The contract's own words for when this is due — the reviewer
              sets the actual calendar date from this, so it sits in plain
              sight rather than buried in the quote. */}
          {ob.due_description && (
            <p className="mt-1.5 text-xs text-slate-700">
              <span className="font-semibold">Contract deadline:</span> {ob.due_description}
            </p>
          )}
          {ob.source_quote && (
            <button type="button" onClick={() => setShowQuote(v => !v)} className="mt-1.5 text-xs text-orange-700 hover:text-orange-800 font-medium">
              {showQuote ? 'Hide the contract language' : 'See the contract language'}
            </button>
          )}
          {showQuote && (
            <blockquote className="mt-2 border-l-2 border-orange-300 pl-3 text-xs text-slate-600 italic whitespace-pre-line">
              “{ob.source_quote}”
              {ob.citation && <span className="block not-italic font-semibold text-slate-400 mt-1">{ob.citation}</span>}
              {!ob.quote_verified && <span className="block not-italic text-amber-700 mt-1">This quote could not be verified word-for-word against the contract — read it in the source before relying on it.</span>}
            </blockquote>
          )}

          {/* The contract's price for missing this — verbatim, cited, quiet
              until asked. Nothing here computes or alarms; the contract
              speaks and the reader decides. */}
          {myPenalties.length > 0 && (
            <details className="mt-2 rounded-lg border border-slate-100 group/pen">
              <summary className="cursor-pointer list-none px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
                <span className="text-slate-300 transition-transform group-open/pen:rotate-90">›</span>
                Penalties associated with this {ob.category === 'dated' ? 'deliverable' : 'condition'}
              </summary>
              <div className="px-3 pb-3 space-y-2">
                {myPenalties.map(p => (
                  <div key={p.id}>
                    <blockquote className="border-l-2 border-slate-300 bg-slate-50 rounded-r-lg pl-3 pr-3 py-2 text-xs text-slate-600 leading-relaxed">
                      “{p.issue_text} — {p.damages_text}”
                    </blockquote>
                    {p.citation && <p className="text-[11px] font-semibold text-slate-400 mt-1">{p.citation}</p>}
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* When-it-happens machinery: the trigger in the contract's words,
              the SLA that runs from it, the report button that turns a real
              event into real dated work, and the ledger of past reports. */}
          {ob.category === 'conditional' && (
            <div className="mt-2">
              {(ob.trigger_text || ob.sla_text) && (
                <p className="text-xs text-slate-600">
                  {ob.trigger_text && <><span className="font-semibold">Trigger:</span> {ob.trigger_text}</>}
                  {ob.sla_text && <>{ob.trigger_text ? ' · ' : ''}<span className="font-semibold">Then:</span> {ob.sla_text}</>}
                </p>
              )}
              {ob.status === 'active' && (
                <ReportEventPanel ob={ob} onReported={onReported} />
              )}
              {reportedEvents.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {reportedEvents.slice(0, 3).map(e => (
                    <p key={e.id} className="text-[11px] text-slate-500">
                      Event on {fmtDate(e.event_date)} reported by {e.reported_by_name || 'a teammate'}
                      {e.computed_due ? <> — duty due {fmtDate(e.computed_due)}</> : null}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {ob.status === 'active' && (
          <div className="text-right flex-shrink-0 text-xs text-slate-500">
            <p className="font-medium text-slate-700">{ob.owner_name || ob.department_name || 'Unassigned'}</p>
            <p className="mt-0.5">
              {ob.category === 'conditional'
                ? 'When it happens'
                : <>{CADENCE_LABEL[ob.cadence] || '—'}{ob.next_due_date ? ` · next ${fmtDate(ob.next_due_date)}` : ''}</>}
            </p>
            {ob.last_evidence_at && <p className="mt-0.5 text-emerald-700">Last evidenced {fmtDate(ob.last_evidence_at)}</p>}
          </div>
        )}
      </div>

      {(suggested || ob.status === 'active') && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Owner</label>
            <select
              value={ob.assigned_to_user_id || ''}
              onChange={e => save({ assigned_to_user_id: e.target.value || null })}
              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white min-w-[160px]"
            >
              <option value="">Pick a person…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          {/* A when-it-happens condition has no schedule — dates and cadence
              would dress an if-then up as standing work. Its whole lifecycle
              is: assign an owner, activate, and report events when reality
              provides them. */}
          {ob.category !== 'conditional' && (
            <>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">How often</label>
                <select
                  value={ob.cadence || ''}
                  onChange={e => save({ cadence: e.target.value || null })}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white"
                >
                  <option value="">Pick…</option>
                  {Object.entries(CADENCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">{ob.cadence === 'once' ? 'Due' : 'First due'}</label>
                <DueDateInput value={ob.next_due_date} onCommit={d => save({ next_due_date: d })} />
                {/* One date used to be all you saw, which read as if quarterly meant
                    a single deadline. The series is real — completing an occurrence
                    schedules the next automatically — so say so, with the dates. */}
                {schedulePreview(ob.cadence, ob.next_due_date) && (
                  <p className="text-[11px] text-slate-500 mt-1.5 max-w-[260px]">
                    {schedulePreview(ob.cadence, ob.next_due_date)}
                  </p>
                )}
              </div>
            </>
          )}
          {ob.category === 'conditional' && (
            <p className="text-[11px] text-slate-500 self-center max-w-[300px]">
              No schedule — this is owed only when the trigger occurs. Activate it with an owner; reporting an event creates the dated duty.
            </p>
          )}
          <div className="flex gap-2 ml-auto">
            {suggested ? (
              <>
                <button type="button" disabled={saving || !ob.assigned_to_user_id}
                  title={!ob.assigned_to_user_id ? 'Pick an owner first' : ''}
                  onClick={() => save({ status: 'active' })}
                  className="px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-semibold disabled:opacity-40">
                  Activate obligation
                </button>
                <button type="button" disabled={saving} onClick={() => save({ status: 'retired' })}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-500 hover:text-slate-700">
                  Not applicable
                </button>
              </>
            ) : (
              <button type="button" disabled={saving} onClick={() => save({ status: 'retired' })}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-500 hover:text-slate-700">
                Retire
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
